import { MouseEvent, ReactElement } from 'react';
import { isValid, parseISO } from 'date-fns';
import { FormControlProps } from '@mui/material/FormControl';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { MobileDatePicker, MobileDatePickerProps } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateView } from '@mui/x-date-pickers/models';

import { ReactComponent as CalendarIcon } from '~/ui/assets/images/calendarIcon.svg';
import {
  PickerDraftActionBar,
  PickerDraftActionsContext,
  usePickerDraft,
} from '~/ui/components/inputs/PickerDraft';

import DatePickerToolbar from './DatePickerToolbar';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './DatePicker.module.scss';

// Kept as 'date' (not 'day') for backward compatibility with existing openTo/views callers
export type LegacyDateView = 'year' | 'month' | 'date';
const toDateView = (view: LegacyDateView): DateView => (view === 'date' ? 'day' : view);

interface IProps {
  size?: FormControlProps['size'];
  label?: string;
  error?: string;
  openTo?: LegacyDateView;
  views?: LegacyDateView[];
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;
  value: Date | string;
  onChange: (date: Date | string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  cb?: (value: Date) => void;
  tabIndex?: number;
  showIcon?: boolean;
  fullWidth?: boolean;
  clearable?: boolean;
  muiProps?: Partial<MobileDatePickerProps>;
}

const DatePickerMUI = ({
  size,
  label,
  error,
  openTo,
  views,
  minDate,
  maxDate,
  dateFormat,
  value,
  onChange,
  tabIndex,
  disabled,
  placeholder,
  className,
  showIcon = true,
  fullWidth = true,
  clearable = false,
  cb,
  muiProps,
}: IProps): ReactElement => {
  const isError = Boolean(error);

  const handleChange = (date: Date | null) => {
    if (!date || !isValid(date)) return '';

    return new Date(date.setHours(12)).toISOString();
  };

  const formatValue = (val: Date | string): Date | null => {
    if (val instanceof Date) return isValid(val) ? val : null;
    const date = parseISO(val);
    return isValid(date) ? date : null;
  };

  const picker = usePickerDraft({
    value: formatValue(value),
    onCommit: date => {
      if (date) cb?.(date);
      onChange(handleChange(date));
    },
    disabled,
  });

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation();
    onChange('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PickerDraftActionsContext.Provider value={picker.actions}>
        <MobileDatePicker
          {...picker.pickerProps}
          label={label}
          openTo={openTo ? toDateView(openTo) : undefined}
          views={views?.map(toDateView)}
          minDate={minDate ? new Date(minDate) : undefined}
          maxDate={maxDate ? new Date(maxDate) : undefined}
          format={dateFormat}
          disabled={disabled}
          className={styles.datePickerControl}
          slots={{
            openPickerIcon: () => <CalendarIcon color={variables.colorRed} />,
            toolbar: DatePickerToolbar,
            actionBar: PickerDraftActionBar,
            ...(showIcon ? {} : { openPickerButton: () => null }),
          }}
          slotProps={{
            textField: {
              variant: 'outlined',
              size,
              fullWidth,
              error: isError,
              helperText: error,
              className,
              ...picker.textFieldProps,
              slotProps: {
                formHelperText: { className: styles.errorText },
                inputLabel: { shrink: true, error: isError },
                htmlInput: { tabIndex: tabIndex ?? -1, placeholder },
              },
            },
            field: clearable ? { clearable, onClear: handleClear } : undefined,
            dialog: picker.dialogProps,
          }}
          {...muiProps}
        />
      </PickerDraftActionsContext.Provider>
    </LocalizationProvider>
  );
};

export default DatePickerMUI;
